/* =========================================================================
   Phrase Studios — Cart drawer extras (Phase 4)
   - <phrase-cart-upsell>: lazy-fetch Shopify product recommendations
     (complementary intent) and render a horizontal rail of cards.
   - data-phrase-cart-gift: gift-wrap toggle, gift note autosave, auto-add
     complimentary gift product when subtotal crosses configured threshold.
   All free, all native Shopify endpoints. No paid apps, no third-party SDKs.
   ========================================================================= */
(function () {
  'use strict';

  const RECS_ENDPOINT = '/recommendations/products';
  const CART_ADD = '/cart/add.js';
  const CART_CHANGE = '/cart/change.js';
  const CART_UPDATE = '/cart/update.js';
  const CART_GET = '/cart.js';

  // ---- Helpers ----
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then((r) => r.json());
  }

  function getCart() {
    return fetch(CART_GET, { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
  }

  function debounce(fn, ms) {
    let t = null;
    return function (...args) {
      if (t) clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function refreshCartDrawer() {
    // Trigger Dawn's cart drawer refresh hook so totals/items re-render.
    const cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.renderContents === 'function') {
      return getCart().then((c) => cd.renderContents(c));
    }
    // Fallback: dispatch the cart:update event the theme listens for.
    document.dispatchEvent(new CustomEvent('cart:update'));
    return Promise.resolve();
  }

  // ==========================================================================
  // <phrase-cart-upsell> — recommendations rail
  // ==========================================================================
  class PhraseCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.fetched = false;
    }

    connectedCallback() {
      const rail = this.querySelector('[data-upsell-rail]');
      if (!rail) return;
      this.rail = rail;

      // Defer the fetch until the rail enters the viewport, so it never blocks
      // the initial drawer open paint.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.fetched) {
              this.fetched = true;
              io.disconnect();
              this.fetchRecs();
            }
          });
        }, { rootMargin: '120px' });
        io.observe(this);
      } else {
        this.fetchRecs();
      }
    }

    fetchRecs() {
      const productId = this.getAttribute('data-product-id');
      const intent = this.getAttribute('data-intent') || 'complementary';
      const limit = this.getAttribute('data-limit') || '6';
      if (!productId) return;

      const url = `${RECS_ENDPOINT}?section_id=phrase-cart-recommendations&product_id=${encodeURIComponent(productId)}&intent=${encodeURIComponent(intent)}&limit=${encodeURIComponent(limit)}`;

      fetch(url, { headers: { 'Accept': 'text/html' } })
        .then((r) => r.text())
        .then((html) => {
          const tmp = document.createElement('div');
          tmp.innerHTML = html.trim();
          // Section response wraps content in a shopify-section div; extract.
          const section = tmp.querySelector('.shopify-section') || tmp;
          const rec = section.querySelector('[data-upsell-rail-content]');
          if (rec && rec.children.length > 0) {
            this.rail.innerHTML = rec.innerHTML;
            this.rail.setAttribute('aria-busy', 'false');
          } else {
            this.hideQuietly();
          }
        })
        .catch(() => this.hideQuietly());
    }

    hideQuietly() {
      this.style.display = 'none';
    }
  }
  if (!customElements.get('phrase-cart-upsell')) {
    customElements.define('phrase-cart-upsell', PhraseCartUpsell);
  }

  // ==========================================================================
  // <phrase-freeship-upsell> — single-card "add one more to ship free" suggestion
  // ==========================================================================
  class PhraseFreeshipUpsell extends HTMLElement {
    constructor() {
      super();
      this.fetched = false;
    }

    connectedCallback() {
      const slot = this.querySelector('[data-freeship-slot]');
      if (!slot) return;
      this.slot = slot;
      // Drawer is hidden until opened; defer the fetch until the element is
      // visible so we never block initial open paint.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.fetched) {
              this.fetched = true;
              io.disconnect();
              this.fetchAndPick();
            }
          });
        }, { rootMargin: '60px' });
        io.observe(this);
      } else {
        this.fetchAndPick();
      }
    }

    fetchAndPick() {
      const productId = this.getAttribute('data-anchor-product-id');
      const remaining = parseInt(this.getAttribute('data-remaining-cents'), 10) || 0;
      if (!productId || remaining <= 0) { this.hideQuietly(); return; }

      const url = `${RECS_ENDPOINT}?section_id=phrase-freeship-upsell&product_id=${encodeURIComponent(productId)}&intent=related&limit=10`;

      fetch(url, { headers: { 'Accept': 'text/html' } })
        .then((r) => r.text())
        .then((html) => {
          const tmp = document.createElement('div');
          tmp.innerHTML = html.trim();
          const section = tmp.querySelector('.shopify-section') || tmp;
          const cards = Array.from(section.querySelectorAll('[data-freeship-card]'));
          // Candidates whose price crosses the threshold. Prefer the cheapest
          // qualifier so the upsell feels generous, not greedy.
          const sized = cards
            .map((c) => ({ el: c, cents: parseInt(c.getAttribute('data-price-cents'), 10) || 0 }))
            .filter((c) => c.cents > 0);
          if (sized.length === 0) { this.hideQuietly(); return; }
          const qualifiers = sized.filter((c) => c.cents >= remaining).sort((a, b) => a.cents - b.cents);
          let pick;
          if (qualifiers.length > 0) {
            pick = qualifiers[0].el;
          } else {
            // No single product crosses the threshold — fall back to the
            // most expensive recommendation and soften the label so the
            // promise stays honest.
            sized.sort((a, b) => b.cents - a.cents);
            pick = sized[0].el;
            const label = this.querySelector('.phrase-drawer__freeship-up-label');
            if (label) label.textContent = 'Add this to get closer to free shipping';
          }
          this.slot.innerHTML = '';
          this.slot.appendChild(pick);
          this.wireAdd(pick);
          this.classList.add('is-ready');
        })
        .catch(() => this.hideQuietly());
    }

    wireAdd(card) {
      const btn = card.querySelector('[data-freeship-add]');
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
        if (!variantId) return;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        postJSON(CART_ADD, { items: [{ id: variantId, quantity: 1 }] })
          .then(refreshCartDrawer)
          .catch(() => {
            btn.disabled = false;
            btn.textContent = 'Add';
          });
      });
    }

    hideQuietly() {
      this.style.display = 'none';
    }
  }
  if (!customElements.get('phrase-freeship-upsell')) {
    customElements.define('phrase-freeship-upsell', PhraseFreeshipUpsell);
  }

  // ==========================================================================
  // Gift options — wrap toggle, note autosave, threshold-unlock auto-gift
  // ==========================================================================
  function initGiftBlock(block) {
    if (!block || block.__phraseGiftInit) return;
    block.__phraseGiftInit = true;

    const wrapToggle = block.querySelector('[data-phrase-giftwrap-toggle]');
    const noteInput = block.querySelector('[data-phrase-giftnote]');
    const thresholdEl = block.querySelector('[data-phrase-gift-threshold]');
    const giftwrapId = block.getAttribute('data-giftwrap-variant-id');
    const giftId = block.getAttribute('data-gift-variant-id');
    const thresholdCents = parseInt(block.getAttribute('data-gift-threshold-cents'), 10) || 0;

    // ---- Gift-wrap toggle ----
    if (wrapToggle && giftwrapId) {
      wrapToggle.addEventListener('change', () => {
        const checked = wrapToggle.checked;
        wrapToggle.disabled = true;
        const op = checked
          ? postJSON(CART_ADD, { items: [{ id: parseInt(giftwrapId, 10), quantity: 1, properties: { _giftwrap: 'true' } }] })
          : removeVariantFromCart(parseInt(giftwrapId, 10));
        op
          .then(refreshCartDrawer)
          .catch(() => { wrapToggle.checked = !checked; })
          .finally(() => { wrapToggle.disabled = false; });
      });
    }

    // ---- Gift note (debounced autosave to cart attribute) ----
    if (noteInput) {
      const save = debounce(() => {
        postJSON(CART_UPDATE, { attributes: { gift_note: noteInput.value || '' } });
      }, 400);
      noteInput.addEventListener('input', save);
    }

    // ---- Threshold-unlock complimentary gift ----
    if (giftId && thresholdCents > 0) {
      reconcileGift(parseInt(giftId, 10), thresholdCents, thresholdEl);
    }
  }

  function removeVariantFromCart(variantId) {
    return getCart().then((cart) => {
      let lineIndex = -1;
      (cart.items || []).forEach((it, i) => {
        if (it.variant_id === variantId) lineIndex = i + 1;
      });
      if (lineIndex < 0) return cart;
      return postJSON(CART_CHANGE, { line: lineIndex, quantity: 0 });
    });
  }

  function reconcileGift(giftVariantId, thresholdCents, thresholdEl) {
    getCart().then((cart) => {
      const subtotal = cart.total_price;
      let giftLineIndex = -1;
      let giftLine = null;
      (cart.items || []).forEach((it, i) => {
        if (it.variant_id === giftVariantId) {
          giftLineIndex = i + 1;
          giftLine = it;
        }
      });
      const qualifies = subtotal >= thresholdCents;

      // Update threshold copy.
      if (thresholdEl) {
        if (qualifies) {
          thresholdEl.classList.add('is-unlocked');
        } else {
          thresholdEl.classList.remove('is-unlocked');
        }
      }

      if (qualifies && !giftLine) {
        return postJSON(CART_ADD, {
          items: [{ id: giftVariantId, quantity: 1, properties: { _phrase_gift: 'true' } }]
        }).then(refreshCartDrawer);
      }
      if (!qualifies && giftLine) {
        return postJSON(CART_CHANGE, { line: giftLineIndex, quantity: 0 }).then(refreshCartDrawer);
      }
    });
  }

  // Wire any gift block currently in the DOM, plus any that appear after a
  // cart re-render (Dawn replaces drawer contents on update).
  function wireAllGiftBlocks() {
    document.querySelectorAll('[data-phrase-cart-gift]').forEach(initGiftBlock);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireAllGiftBlocks, { once: true });
  } else {
    wireAllGiftBlocks();
  }

  // Re-wire on cart drawer mutations (Dawn re-renders the drawer body on add/update).
  const drawer = document.querySelector('cart-drawer');
  if (drawer && 'MutationObserver' in window) {
    const mo = new MutationObserver(() => {
      wireAllGiftBlocks();
    });
    mo.observe(drawer, { childList: true, subtree: true });
  }

  // ==========================================================================
  // Card quick-add — size pills and the hover "+" on product cards add the
  // chosen variant to the cart and open the drawer. Delegated on document so
  // it works on collection pages, the homepage, and any re-rendered cards.
  // ==========================================================================
  function cartDrawerEl() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function quickAddVariant(variantId, trigger) {
    if (!variantId) return;
    const cart = cartDrawerEl();
    const body = { items: [{ id: variantId, quantity: 1 }] };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id).join(',');
      body.sections_url = window.location.pathname;
    }
    if (trigger) {
      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
    }
    postJSON(CART_ADD, body)
      .then((response) => {
        // Shopify returns a `status` only on error (e.g. sold out / not addable).
        if (response && response.status) {
          const handle = trigger && trigger.getAttribute('data-product-handle');
          if (handle) window.location.href = '/products/' + handle + '?variant=' + variantId;
          return;
        }
        if (cart && typeof cart.renderContents === 'function' && response.sections) {
          cart.renderContents(response);
        } else {
          refreshCartDrawer().then(function () {
            if (cart && typeof cart.open === 'function') cart.open();
          });
        }
        if (trigger) {
          trigger.classList.add('is-added');
          setTimeout(function () { trigger.classList.remove('is-added'); }, 1400);
        }
      })
      .catch(function () {
        const handle = trigger && trigger.getAttribute('data-product-handle');
        if (handle) window.location.href = '/products/' + handle;
      })
      .then(function () {
        if (trigger) {
          trigger.classList.remove('is-loading');
          trigger.removeAttribute('aria-busy');
        }
      });
  }

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-phrase-quick-add]');
    if (!btn) return;
    e.preventDefault();
    quickAddVariant(btn.getAttribute('data-variant-id'), btn);
  });
})();
